import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Briefcase,
  CircleDot,
  LayoutGrid,
  List,
  Mail,
  MoreVertical,
  Plus,
  Search,
  ShieldCheck,
  UserRound,
  UserRoundX,
  Users,
  UsersRound,
} from "lucide-react";
import client, { unwrap } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { initials, ROLE_LABELS } from "@/lib/format";
import KpiCard from "@/components/dashboard/KpiCard";
import Pagination from "@/components/Pagination";
import AddMemberDialog from "@/components/team/AddMemberDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

// Roles a shop admin may assign (mirrors CreateUserDto on the backend).
const ASSIGNABLE_ROLES = ["ACCOUNT_MANAGER", "SALES_EXECUTIVE", "EXTERNAL_ARCHITECT", "EXTERNAL_DESIGNER"];
const EXTERNAL_ROLES = ["EXTERNAL_ARCHITECT", "EXTERNAL_DESIGNER"];
const ROLE_ORDER = ["SHOP_ADMIN", ...ASSIGNABLE_ROLES];
const PAGE_SIZE = 8;
const ALL = "all";
const VIEW_KEY = "hi_team_view";

const ROLE_TITLE = {
  ...ROLE_LABELS,
  EXTERNAL_ARCHITECT: "Design Partner",
  EXTERNAL_DESIGNER: "Interior Designer",
};

// Shared by role pills and avatars so each role keeps one colour.
const ROLE_STYLE = {
  SHOP_ADMIN: "bg-accent/10 text-accent",
  ACCOUNT_MANAGER: "bg-primary/10 text-primary",
  SALES_EXECUTIVE: "bg-success/10 text-success",
  EXTERNAL_ARCHITECT: "bg-chart-3/10 text-chart-3",
  EXTERNAL_DESIGNER: "bg-warning/15 text-[hsl(41_60%_32%)]",
};

const STATUS_LABEL = { ACTIVE: "Active", INVITED: "Invited", DISABLED: "Inactive" };
const STATUS_STYLE = {
  ACTIVE: { pill: "bg-success/10 text-success", dot: "bg-success" },
  INVITED: { pill: "bg-warning/15 text-[hsl(41_60%_32%)]", dot: "bg-warning" },
  DISABLED: { pill: "bg-secondary text-muted-foreground", dot: "bg-muted-foreground/60" },
};

const primaryRole = (u) => ROLE_ORDER.find((r) => u.roleNames?.includes(r)) || u.roleNames?.[0];
const isExternal = (u) => u.roleNames?.some((r) => EXTERNAL_ROLES.includes(r));

function readView() {
  try {
    return localStorage.getItem(VIEW_KEY) === "grid" ? "grid" : "list";
  } catch {
    return "list";
  }
}

function MemberAvatar({ u, size = "h-10 w-10" }) {
  return (
    <span
      className={cn(
        "rounded-full font-serif text-sm flex items-center justify-center shrink-0",
        size,
        ROLE_STYLE[primaryRole(u)] || "bg-secondary text-foreground/80",
      )}
    >
      {initials(u.name)}
    </span>
  );
}

function RolePill({ role }) {
  if (!role) return <span className="text-muted-foreground">—</span>;
  return (
    <span
      className={cn(
        "inline-flex rounded-md px-2.5 py-1 font-mono-spec text-[11px] tracking-wide whitespace-nowrap",
        ROLE_STYLE[role] || "bg-secondary text-muted-foreground",
      )}
    >
      {role}
    </span>
  );
}

function StatusPill({ status }) {
  const style = STATUS_STYLE[status] || STATUS_STYLE.DISABLED;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap", style.pill)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", style.dot)} />
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function MemberMenu({ u, isSelf, onToggleStatus }) {
  const active = u.status === "ACTIVE";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`More actions for ${u.name}`}
          data-testid={`team-member-menu-${u.id}`}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <a href={`mailto:${u.email}`}>
            <Mail className="h-4 w-4 mr-2" /> Email member
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigator.clipboard?.writeText(u.email).then(() => toast.success("Email copied"))}
        >
          Copy email
        </DropdownMenuItem>
        {!isSelf && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid={`toggle-user-status-${u.id}`}
              className={active ? "text-danger focus:text-danger" : ""}
              onClick={() => onToggleStatus(u)}
            >
              {active ? "Deactivate" : "Activate"}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MemberName({ u, isSelf }) {
  const role = primaryRole(u);
  return (
    <div className="min-w-0">
      <p className="font-serif text-[15px] text-foreground truncate">
        {u.name}
        {isSelf && <span className="ml-2 align-middle font-sans text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">You</span>}
      </p>
      {role && <p className="text-xs text-muted-foreground truncate">{ROLE_TITLE[role] || role}</p>}
    </div>
  );
}

function FilterSelect({ icon: Icon, value, onChange, testId, allLabel, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="w-full sm:w-[170px] rounded-lg bg-card">
        <span className="flex items-center gap-2 truncate">
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function TeamPage() {
  const queryClient = useQueryClient();
  const { user: me } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(() => new Set());
  const [view, setViewState] = useState(readView);

  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: () => client.get("/users").then(unwrap) });

  const statusMutation = useMutation({
    mutationFn: ({ ids, status }) => Promise.all(ids.map((id) => client.patch(`/users/${id}/status`, { status }))),
    onSuccess: (_, { ids, status }) => {
      toast.success(`${ids.length === 1 ? "Member" : `${ids.length} members`} ${status === "ACTIVE" ? "activated" : "deactivated"}`);
      setSelected(new Set());
    },
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed to update status"),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });

  const setView = (v) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* view preference is optional */
    }
  };

  const isSelf = (u) => String(u.id) === String(me?.id);

  const members = useMemo(
    () =>
      [...users].sort(
        (a, b) =>
          ROLE_ORDER.indexOf(primaryRole(a)) - ROLE_ORDER.indexOf(primaryRole(b)) || (a.name || "").localeCompare(b.name || ""),
      ),
    [users],
  );

  const roleOptions = useMemo(
    () =>
      ROLE_ORDER.filter((r) => members.some((u) => u.roleNames?.includes(r))).map((r) => ({ value: r, label: ROLE_LABELS[r] || r })),
    [members],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return members.filter(
      (u) =>
        (!term ||
          [u.name, u.email, ...(u.roleNames || []), ...(u.roleNames || []).map((r) => ROLE_LABELS[r])].some((v) =>
            v?.toLowerCase().includes(term),
          )) &&
        (roleFilter === ALL || u.roleNames?.includes(roleFilter)) &&
        (statusFilter === ALL || u.status === statusFilter) &&
        (typeFilter === ALL || (typeFilter === "external") === isExternal(u)),
    );
  }, [members, search, roleFilter, statusFilter, typeFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const filtersActive = search || roleFilter !== ALL || statusFilter !== ALL || typeFilter !== ALL;

  const internalCount = users.filter((u) => !isExternal(u)).length;
  const externalCount = users.length - internalCount;
  const inactiveCount = users.filter((u) => u.status !== "ACTIVE").length;

  const resetPage = (fn) => (v) => {
    fn(v);
    setPage(1);
  };
  const clearFilters = () => {
    setSearch("");
    setRoleFilter(ALL);
    setStatusFilter(ALL);
    setTypeFilter(ALL);
    setPage(1);
  };
  const kpiFilter = (type, status) => () => {
    const isActive = typeFilter === type && statusFilter === status;
    setTypeFilter(isActive ? ALL : type);
    setStatusFilter(isActive ? ALL : status);
    setRoleFilter(ALL);
    setPage(1);
  };

  // Selection never includes the signed-in admin, so bulk actions can't lock them out.
  const selectable = pageRows.filter((u) => !isSelf(u));
  const allOnPageSelected = selectable.length > 0 && selectable.every((u) => selected.has(u.id));
  const someOnPageSelected = selectable.some((u) => selected.has(u.id));
  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const togglePage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      selectable.forEach((u) => (allOnPageSelected ? next.delete(u.id) : next.add(u.id)));
      return next;
    });
  const selectedUsers = users.filter((u) => selected.has(u.id));
  const bulkSetStatus = (status) => {
    const ids = selectedUsers.filter((u) => u.status !== status).map((u) => u.id);
    if (ids.length === 0) {
      toast.info(`Selected members are already ${status === "ACTIVE" ? "active" : "inactive"}`);
      return;
    }
    statusMutation.mutate({ ids, status });
  };
  const toggleStatus = (u) => statusMutation.mutate({ ids: [u.id], status: u.status === "ACTIVE" ? "DISABLED" : "ACTIVE" });

  return (
    <div data-testid="team-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-[2.6rem] leading-tight text-foreground">Team</h1>
          <p className="text-muted-foreground mt-1">Internal users and external architects/designers who collaborate on your projects.</p>
        </div>
        <Button data-testid="add-team-member-button" className="rounded-full px-5 self-start sm:self-auto bg-accent hover:bg-accent/90 text-accent-foreground" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" /> Add Member
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          testId="kpi-team-total"
          icon={UsersRound}
          tone="green"
          label="Total Members"
          value={users.length}
          sublabel={`${users.length - inactiveCount} active`}
          onClick={clearFilters}
          active={false}
        />
        <KpiCard
          testId="kpi-team-internal"
          icon={Users}
          tone="terracotta"
          label="Internal Users"
          value={internalCount}
          sublabel="Shop staff"
          onClick={kpiFilter("internal", ALL)}
          active={typeFilter === "internal" && statusFilter === ALL}
        />
        <KpiCard
          testId="kpi-team-external"
          icon={Briefcase}
          tone="slate"
          label="External Partners"
          value={externalCount}
          sublabel="Architects & designers"
          onClick={kpiFilter("external", ALL)}
          active={typeFilter === "external" && statusFilter === ALL}
        />
        <KpiCard
          testId="kpi-team-inactive"
          icon={UserRoundX}
          tone="gold"
          label={inactiveCount === 1 ? "Inactive Member" : "Inactive Members"}
          value={inactiveCount}
          sublabel="Can't sign in"
          onClick={kpiFilter(ALL, "DISABLED")}
          active={statusFilter === "DISABLED" && typeFilter === ALL}
        />
      </div>

      <section className="bg-card border border-border rounded-2xl overflow-hidden">
        {/* Filters */}
        <div className="flex flex-col xl:flex-row xl:items-center gap-3 p-4 border-b border-border">
          <div className="relative flex-1 xl:max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="team-search-input"
              value={search}
              onChange={(e) => resetPage(setSearch)(e.target.value)}
              placeholder="Search by name, email, or role…"
              className="w-full rounded-full border border-border bg-secondary/70 pl-10 pr-4 py-2.5 text-sm placeholder:text-muted-foreground focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 flex-1">
            <FilterSelect icon={ShieldCheck} value={roleFilter} onChange={resetPage(setRoleFilter)} testId="team-role-filter" allLabel="All roles" options={roleOptions} />
            <FilterSelect
              icon={CircleDot}
              value={statusFilter}
              onChange={resetPage(setStatusFilter)}
              testId="team-status-filter"
              allLabel="All statuses"
              options={[
                { value: "ACTIVE", label: "Active" },
                { value: "DISABLED", label: "Inactive" },
              ]}
            />
            <FilterSelect
              icon={UserRound}
              value={typeFilter}
              onChange={resetPage(setTypeFilter)}
              testId="team-type-filter"
              allLabel="All types"
              options={[
                { value: "internal", label: "Internal" },
                { value: "external", label: "External" },
              ]}
            />
            {filtersActive && (
              <button data-testid="team-clear-filters" onClick={clearFilters} className="text-sm font-medium text-accent hover:underline px-2 self-start sm:self-auto">
                Clear filters
              </button>
            )}
            <div className="hidden md:flex sm:ml-auto rounded-lg border border-border p-0.5" role="group" aria-label="Layout">
              {[
                { v: "grid", Icon: LayoutGrid, label: "Grid view" },
                { v: "list", Icon: List, label: "List view" },
              ].map(({ v, Icon, label }) => (
                <button
                  key={v}
                  aria-label={label}
                  aria-pressed={view === v}
                  data-testid={`team-view-${v}`}
                  onClick={() => setView(v)}
                  className={cn(
                    "h-8 w-9 rounded-md flex items-center justify-center transition-colors",
                    view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary",
                  )}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Bulk actions */}
        {selected.size > 0 && (
          <div data-testid="team-bulk-bar" className="flex flex-wrap items-center gap-3 px-5 py-2.5 bg-primary/5 border-b border-border text-sm">
            <span className="font-medium text-foreground">{selected.size} selected</span>
            <Button size="sm" variant="outline" disabled={statusMutation.isPending} onClick={() => bulkSetStatus("ACTIVE")}>
              Activate
            </Button>
            <Button size="sm" variant="outline" className="text-danger hover:text-danger" disabled={statusMutation.isPending} onClick={() => bulkSetStatus("DISABLED")}>
              Deactivate
            </Button>
            <button onClick={() => setSelected(new Set())} className="ml-auto text-muted-foreground hover:text-foreground">
              Clear selection
            </button>
          </div>
        )}

        {/* Desktop table */}
        {view === "list" && (
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary/50 text-muted-foreground font-mono-spec text-[11px] uppercase tracking-wider text-left">
                  <th className="py-3 pl-5 pr-2 w-10">
                    <Checkbox
                      aria-label="Select all on this page"
                      data-testid="team-select-all"
                      checked={allOnPageSelected ? true : someOnPageSelected ? "indeterminate" : false}
                      disabled={selectable.length === 0}
                      onCheckedChange={togglePage}
                    />
                  </th>
                  <th className="py-3 px-3 font-semibold">Member</th>
                  <th className="py-3 px-3 font-semibold">Email</th>
                  <th className="py-3 px-3 font-semibold">Role</th>
                  <th className="py-3 px-3 font-semibold">Type</th>
                  <th className="py-3 px-3 font-semibold">Status</th>
                  <th className="py-3 pl-3 pr-5 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pageRows.map((u) => (
                  <tr
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    className={cn("transition-colors", selected.has(u.id) ? "bg-primary/5" : "hover:bg-secondary/40")}
                  >
                    <td className="py-3.5 pl-5 pr-2">
                      <Checkbox
                        aria-label={`Select ${u.name}`}
                        checked={selected.has(u.id)}
                        disabled={isSelf(u)}
                        onCheckedChange={() => toggleOne(u.id)}
                      />
                    </td>
                    <td className="py-3.5 px-3">
                      <div className="flex items-center gap-3">
                        <MemberAvatar u={u} />
                        <MemberName u={u} isSelf={isSelf(u)} />
                      </div>
                    </td>
                    <td className="py-3.5 px-3 text-foreground/85 max-w-[260px] truncate">{u.email}</td>
                    <td className="py-3.5 px-3"><RolePill role={primaryRole(u)} /></td>
                    <td className="py-3.5 px-3 text-foreground/85">{isExternal(u) ? "External" : "Internal"}</td>
                    <td className="py-3.5 px-3"><StatusPill status={u.status} /></td>
                    <td className="py-3.5 pl-3 pr-5 text-right">
                      <MemberMenu u={u} isSelf={isSelf(u)} onToggleStatus={toggleStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Grid (desktop) and cards (always on mobile) */}
        <ul
          className={cn(
            "grid gap-4 p-4 sm:grid-cols-2",
            view === "grid" ? "lg:grid-cols-3 2xl:grid-cols-4" : "md:hidden",
          )}
        >
          {pageRows.map((u) => (
            <li
              key={u.id}
              data-testid={`team-card-${u.id}`}
              className={cn(
                "relative rounded-2xl border p-4 transition-colors",
                selected.has(u.id) ? "border-primary/40 bg-primary/5" : "border-border hover:border-primary/30",
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <MemberAvatar u={u} size="h-12 w-12" />
                  <MemberName u={u} isSelf={isSelf(u)} />
                </div>
                <div className="flex items-center gap-1">
                  {!isSelf(u) && (
                    <Checkbox aria-label={`Select ${u.name}`} checked={selected.has(u.id)} onCheckedChange={() => toggleOne(u.id)} />
                  )}
                  <MemberMenu u={u} isSelf={isSelf(u)} onToggleStatus={toggleStatus} />
                </div>
              </div>
              <p className="mt-3 flex items-center gap-2 text-sm text-foreground/85 truncate">
                <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> <span className="truncate">{u.email}</span>
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <RolePill role={primaryRole(u)} />
                <span className="text-xs text-muted-foreground">{isExternal(u) ? "External" : "Internal"}</span>
                <span className="ml-auto"><StatusPill status={u.status} /></span>
              </div>
            </li>
          ))}
        </ul>

        {!isLoading && filtered.length === 0 && (
          <div className="py-14 text-center">
            <p className="text-sm text-muted-foreground">
              {users.length === 0 ? "No team members yet. Add your first one to get started." : "No members match these filters."}
            </p>
            {filtersActive && (
              <button onClick={clearFilters} className="mt-2 text-sm font-medium text-accent hover:underline">Clear filters</button>
            )}
          </div>
        )}

        {filtered.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length} member
              {filtered.length === 1 ? "" : "s"}
            </p>
            <Pagination testId="team-pagination" page={currentPage} pageCount={pageCount} onChange={setPage} />
          </div>
        )}
      </section>

      <AddMemberDialog open={open} setOpen={setOpen} />
    </div>
  );
}
